import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { AttendanceRegistrationTypeDto, AttendanceStateDto } from '../../../../../api-service/clients';
import { CitizenLessonAttendanceState, RegistrationDetailsEdit } from '../interfaces';
import { TimePicker, TimePickerValue } from '../../../../../components/time-picker/time-picker';
import { ReadonlyInput } from '../../../../../components/readonly-input';
import { TypographySettings } from '../../../../../components/config';
import {
  RegistrationTimeWindow,
  checkInTimeToTimePickerValue,
  formatCheckInTime,
  getRegistrationTimeWindow,
  isRegistrationTimeWithinWindow,
  timePickerValueToCheckInTime,
} from './attendance-time';
import { getAttendanceStateDialogSentence, getCommentFieldLabel, getRegistrationTimeFieldLabel } from '../../../attendance-state-texts';
import { MaxLengthTextField } from '../../../../../components/text-field';

const maxCommentLength = 500;

interface IAttendanceCommentDialogProps {
  open: boolean;
  citizenLessonAttendance: CitizenLessonAttendanceState;
  lessonBeginAt: Date | string;
  lessonEndAt: Date | string;
  showStartAttendanceRegistration: boolean;
  showEndAttendanceRegistration: boolean;
  readOnly?: boolean;
  onClose: () => void;
  onSave: (citizenId: string, start?: RegistrationDetailsEdit, end?: RegistrationDetailsEdit) => void;
}

interface ICommentSectionProps {
  registrationType: AttendanceRegistrationTypeDto;
  status?: AttendanceStateDto;
  heading: string;
  comment: string;
  time: TimePickerValue;
  timeWindow: RegistrationTimeWindow;
  readOnly: boolean;
  onCommentChange: (value: string) => void;
  onTimeChange: (value: TimePickerValue) => void;
}

function getTimeOutsideWindowMessage(timeWindow: RegistrationTimeWindow) {
  return `Tidspunktet skal ligge mellem ${formatCheckInTime(timeWindow.beginAt)} og ${formatCheckInTime(timeWindow.endAt)}`;
}

function CommentSection(props: ICommentSectionProps) {
  const { registrationType, status, heading, comment, time, timeWindow, readOnly, onCommentChange, onTimeChange } = props;
  const isRegistered = status !== undefined;
  const statusText = isRegistered ? getAttendanceStateDialogSentence(status, registrationType) : 'Ikke angivet';
  const commentLabel = isRegistered ? getCommentFieldLabel(status, registrationType) : 'Kommentar';
  const showTimeField = status === AttendanceStateDto.NotOnTime;
  const timeIsOutsideWindow = !isRegistrationTimeWithinWindow(time, timeWindow);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography fontWeight={TypographySettings.DefaultFontSemiBold}>{heading}</Typography>
        <Typography>Status: {statusText}</Typography>
      </Box>
      {showTimeField && (
        <TimePicker
          label={getRegistrationTimeFieldLabel(registrationType)}
          value={time}
          onChange={onTimeChange}
          readOnly={readOnly}
          variant="outlined"
          fullWidth
          minTime={timeWindow.beginAt}
          maxTime={timeWindow.endAt}
          error={timeIsOutsideWindow}
          errorText={timeIsOutsideWindow ? getTimeOutsideWindowMessage(timeWindow) : undefined}
        />
      )}
      <MaxLengthTextField
        variant="outlined"
        label={commentLabel}
        value={comment}
        onChange={event => onCommentChange(event.target.value)}
        disabled={!isRegistered}
        InputProps={{ readOnly }}
        multiline
        minRows={2}
        fullWidth
        maxLength={maxCommentLength}
      />
    </Box>
  );
}

export function AttendanceCommentDialog(props: IAttendanceCommentDialogProps) {
  const {
    open,
    citizenLessonAttendance,
    lessonBeginAt,
    lessonEndAt,
    showStartAttendanceRegistration,
    showEndAttendanceRegistration,
    readOnly = false,
    onClose,
    onSave,
  } = props;
  const { startRegistration, endRegistration } = citizenLessonAttendance;
  const startStatus = startRegistration?.status;
  const endStatus = endRegistration?.status;
  const isNotRelevant = startStatus === AttendanceStateDto.NotRelevant || endStatus === AttendanceStateDto.NotRelevant;
  const showEndCommentSection = isNotRelevant ? !showStartAttendanceRegistration : showEndAttendanceRegistration;
  const timeWindow = getRegistrationTimeWindow(citizenLessonAttendance, lessonBeginAt, lessonEndAt);

  const [startComment, setStartComment] = useState('');
  const [endComment, setEndComment] = useState('');
  const [startTime, setStartTime] = useState<TimePickerValue>(undefined);
  const [endTime, setEndTime] = useState<TimePickerValue>(undefined);

  useEffect(() => {
    if (open) {
      setStartComment(startRegistration?.commentText ?? '');
      setEndComment(endRegistration?.commentText ?? '');
      setStartTime(checkInTimeToTimePickerValue(startRegistration?.checkInTime));
      setEndTime(checkInTimeToTimePickerValue(endRegistration?.checkInTime));
    }
  }, [open]);

  const hasTooLongComment =
    (showStartAttendanceRegistration && startComment.length > maxCommentLength) || (showEndCommentSection && endComment.length > maxCommentLength);
  const hasTimeOutsideWindow =
    (showStartAttendanceRegistration && startStatus === AttendanceStateDto.NotOnTime && !isRegistrationTimeWithinWindow(startTime, timeWindow)) ||
    (showEndAttendanceRegistration && endStatus === AttendanceStateDto.NotOnTime && !isRegistrationTimeWithinWindow(endTime, timeWindow));

  function buildDetails(status: AttendanceStateDto | undefined, comment: string, time: TimePickerValue, baseCheckInTime?: Date | string): RegistrationDetailsEdit | undefined {
    if (status === undefined) {
      return undefined;
    }
    const checkInTime = status === AttendanceStateDto.NotOnTime ? timePickerValueToCheckInTime(baseCheckInTime, time) : undefined;
    return { comment, checkInTime };
  }

  function handleSave() {
    if (isNotRelevant) {
      const notRelevantComment = showStartAttendanceRegistration ? startComment : endComment;
      onSave(
        citizenLessonAttendance.citizenId,
        showStartAttendanceRegistration && startStatus !== undefined ? { comment: notRelevantComment } : undefined,
        showEndAttendanceRegistration && endStatus !== undefined ? { comment: notRelevantComment } : undefined
      );
    } else {
      onSave(
        citizenLessonAttendance.citizenId,
        showStartAttendanceRegistration ? buildDetails(startStatus, startComment, startTime, startRegistration?.checkInTime) : undefined,
        showEndAttendanceRegistration ? buildDetails(endStatus, endComment, endTime, endRegistration?.checkInTime) : undefined
      );
    }
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Tilføj kommentar til fremmøde</DialogTitle>
      <DialogContent sx={{ overflowY: 'auto' }}>
        <Box display="flex" flexDirection="column" gap={3} paddingTop={1}>
          <ReadonlyInput label="Borger" value={citizenLessonAttendance.citizenName} removeMarginLeft />
          {showStartAttendanceRegistration && (
            <CommentSection
              registrationType={AttendanceRegistrationTypeDto.Start}
              status={startStatus}
              heading="Ved lektionens start"
              comment={startComment}
              time={startTime}
              timeWindow={timeWindow}
              readOnly={readOnly}
              onCommentChange={setStartComment}
              onTimeChange={setStartTime}
            />
          )}
          {showEndCommentSection && (
            <CommentSection
              registrationType={AttendanceRegistrationTypeDto.End}
              status={endStatus}
              heading="Ved lektionens afslutning"
              comment={endComment}
              time={endTime}
              timeWindow={timeWindow}
              readOnly={readOnly}
              onCommentChange={setEndComment}
              onTimeChange={setEndTime}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {!readOnly && (
          <Button variant="contained" onClick={handleSave} disabled={hasTooLongComment || hasTimeOutsideWindow}>
            OK
          </Button>
        )}
        <Button variant="outlined" onClick={onClose}>
          {readOnly ? 'Luk' : 'Afbryd'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
